import { Controller } from '@hotwired/stimulus'

// Submit feedback for the ERB auth forms (login + signup). Progressive
// enhancement: each form is a plain POST that works without JS; with JS the
// CTA disables and relabels ("Entrando…" / "Creando…") while the request is
// in flight. `pageshow` restores the resting state when the browser serves
// the page from bfcache (back button) — that path skips a fresh render and
// would otherwise leave the button stuck disabled. The pending label arrives
// via a Stimulus value so the copy stays in es-MX.yml.
export default class extends Controller<HTMLFormElement> {
  static values = { pendingLabel: String }

  declare readonly pendingLabelValue: string
  declare readonly hasPendingLabelValue: boolean

  private restingLabel = ''
  private stuckTimer: number | undefined
  private onPageshow = (event: PageTransitionEvent) => {
    if (event.persisted) this.reset()
  }

  connect() {
    // Sync marker so a system test can wait until the handler is wired before
    // clicking (same pattern as confirm_controller). Derived from the
    // identifier so a rename can never strand a stale attribute name.
    this.element.setAttribute(`data-${this.identifier}-ready`, 'true')
    window.addEventListener('pageshow', this.onPageshow)
  }

  disconnect() {
    window.removeEventListener('pageshow', this.onPageshow)
    window.clearTimeout(this.stuckTimer)
  }

  submitting() {
    const submit = this.submitButton
    if (!submit || !this.hasPendingLabelValue || !this.pendingLabelValue) return
    this.restingLabel = submit.textContent ?? ''
    submit.textContent = this.pendingLabelValue
    // Disabling here runs after the submit event has dispatched, so it shows
    // progress without cancelling the submission.
    submit.disabled = true
    // Self-heal for aborted navigations (Esc/Stop/failed load keep this
    // document alive, so pageshow never fires): re-enable after 15s. A
    // successful navigation tears the document down before the timer runs.
    this.stuckTimer = window.setTimeout(() => this.reset(), 15_000)
  }

  private reset() {
    window.clearTimeout(this.stuckTimer)
    this.stuckTimer = undefined
    const submit = this.submitButton
    if (!submit) return
    submit.disabled = false
    if (this.restingLabel) submit.textContent = this.restingLabel
  }

  private get submitButton(): HTMLButtonElement | null {
    return this.element.querySelector<HTMLButtonElement>(
      'button[type="submit"]',
    )
  }
}
